import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { colors, glassTextures, glazingTypes } from '../data/mockData';
import ColorPicker from './ColorPicker';
import { useTranslation } from '../context/TranslationContext';

const SchuifpuiOptionsPanel = ({ config, onConfigChange }) => {
  const { t } = useTranslation();
  
  // Helper function to translate glass texture and glazing type names
  const translateGlassTextureName = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato')
    };
    return translations[id] || id;
  };
  
  const translateGlassTextureDescription = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlassDesc'),
      'chinchilla': t('options.chinchillaDesc'),
      'satinato': t('options.satinatoDesc')
    };
    return translations[id] || '';
  };
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeDescription = (id) => {
    const translations = {
      'double': t('options.doubleGlass24Desc'),
      'triple': t('options.doubleGlass36Desc'),
      'double-24': t('options.doubleGlass24Desc'),
      'double-36': t('options.doubleGlass36Desc')
    };
    return translations[id] || '';
  };
  const profileSystems = [
    { 
      id: 'synego-1', 
      name: t('options.recessedProfile'),
      description: t('options.recessedProfileDesc'),
      logo: process.env.PUBLIC_URL + '/images/synego.jpg',
      color: '#E30613'
    }
  ];

  const slidingPositions = [
    { id: 'left', name: t('options.slidingLeft'), description: t('options.slidingLeftDesc'), image: process.env.PUBLIC_URL + '/images/slide-left.png' },
    { id: 'right', name: t('options.slidingRight'), description: t('options.slidingRightDesc'), image: process.env.PUBLIC_URL + '/images/slide-right.png' },
    { id: 'center', name: t('options.slidingCenter'), description: t('options.slidingCenterDesc'), image: process.env.PUBLIC_URL + '/images/slide-center.png' }
  ];

  const thresholdHeights = [
    { id: '30mm', name: '30 mm', value: 30 },
    { id: '55mm', name: '55 mm', value: 55 }
  ];

  const [widthInput, setWidthInput] = useState(String(config.width));
  const [heightInput, setHeightInput] = useState(String(config.height));

  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);

  const commitDimension = (field, rawValue, min, max) => {
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    onConfigChange(field, clamped);
  };

  return (
    <div className="h-full overflow-y-auto p-6 bg-white border-r border-gray-200">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('options.slidingDoorConfiguration')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('options.configureSlidingDoor')}</p>
        </div>

        {/* Dimensions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.dimensions')}</CardTitle>
            <CardDescription className="text-xs">{t('options.setDimensions')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="door-width" className="text-sm font-medium">{t('options.width')}</Label>
                <Input
                  id="door-width"
                  type="number"
                  value={widthInput}
                  onChange={(e) => setWidthInput(e.target.value)}
                  onBlur={() => commitDimension('width', widthInput, 500, 4000)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('width', widthInput, 500, 4000); }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="door-height" className="text-sm font-medium">{t('options.height')}</Label>
                <Input
                  id="door-height"
                  type="number"
                  value={heightInput}
                  onChange={(e) => setHeightInput(e.target.value)}
                  onBlur={() => commitDimension('height', heightInput, 500, 4000)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('height', heightInput, 500, 4000); }}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile System */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.profileSystem')}</CardTitle>
            <CardDescription className="text-xs">We only offer the sliding door with the robust sliding door profile from Schmidt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {profileSystems.map(system => (
                <button
                  key={system.id}
                  onClick={() => onConfigChange('profileSystem', system.id)}
                  className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                    config.profileSystem === system.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full h-20 mb-2 rounded flex items-center justify-center p-2 ${
                    config.profileSystem === system.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img 
                      src={system.logo} 
                      alt={system.name}
                      className="max-h-full max-w-full object-contain"
                      style={{ 
                        display: 'block',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) {
                          fallback.style.display = 'flex';
                          fallback.style.alignItems = 'center';
                          fallback.style.justifyContent = 'center';
                        }
                      }}
                    />
                    <div 
                      className="hidden text-xl font-bold"
                      style={{ color: system.color || '#666' }}
                    >
                      {system.name}
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-gray-900 text-center">{system.name}</div>
                  {system.description && (
                    <div className="text-[10px] text-gray-500 text-center mt-0.5">{system.description}</div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sliding Position */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.slidingPosition')}</CardTitle>
            <CardDescription className="text-xs">Do you want the sliding section viewed from inside in the left part, right part, or center?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              {slidingPositions.map((position) => (
                <button
                  key={position.id}
                  onClick={() => {
                    onConfigChange('slidingPosition', position.id);
                    // Set width to 3000mm when "Sliding Section Right" is selected
                    if (position.id === 'right') {
                      onConfigChange('width', 3000);
                      setWidthInput('3000');
                    }
                  }}
                  className={`relative rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 overflow-hidden ${
                    config.slidingPosition === position.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full aspect-video flex items-center justify-center p-2 ${
                    config.slidingPosition === position.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img
                      src={position.image}
                      alt={position.name}
                      className="max-h-full max-w-full object-contain w-full h-full"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className={`text-xs font-semibold text-center py-2 px-1 ${
                    config.slidingPosition === position.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{position.name}</div>
                  {position.description && (
                    <div className="text-[10px] text-gray-500 text-center px-1 pb-2">{position.description}</div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Threshold */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.threshold')}</CardTitle>
            <CardDescription className="text-xs">Do you want to set the threshold height at 30 mm or 55 mm for the door?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {thresholdHeights.map((height) => (
                <button
                  key={height.id}
                  onClick={() => onConfigChange('thresholdHeight', height.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    config.thresholdHeight === height.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    config.thresholdHeight === height.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{height.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Lintels */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.lintels')}</CardTitle>
            <CardDescription className="text-xs">
              Do you want lintels for this sliding door?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onConfigChange('lintels', false)}
                className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                  !config.lintels
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`text-sm font-medium ${
                  !config.lintels ? 'text-orange-600' : 'text-gray-900'
              }`}>{t('options.no')}</div>
            </button>
            <button
              onClick={() => onConfigChange('lintels', true)}
              className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                config.lintels
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className={`text-sm font-medium ${
                config.lintels ? 'text-orange-600' : 'text-gray-900'
              }`}>{t('options.yes')}</div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Glass Texture - Left Part */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glassLeftPart')}</CardTitle>
            <CardDescription className="text-xs">Select the desired glass type for the left part</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glassTextures.map(texture => (
                <button
                  key={texture.id}
                  onClick={() => onConfigChange('leftGlassTexture', texture.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.leftGlassTexture === texture.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {texture.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={texture.image}
                        alt={texture.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.leftGlassTexture === texture.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlassTextureName(texture.id)}
                    </div>
                    {texture.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlassTextureDescription(texture.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Glass Texture - Right Part */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glassRightPart')}</CardTitle>
            <CardDescription className="text-xs">Select the desired glass type for the right part</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glassTextures.map(texture => (
                <button
                  key={texture.id}
                  onClick={() => onConfigChange('rightGlassTexture', texture.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.rightGlassTexture === texture.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {texture.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={texture.image}
                        alt={texture.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.rightGlassTexture === texture.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlassTextureName(texture.id)}
                    </div>
                    {texture.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlassTextureDescription(texture.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Glazing Type - Right Part */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glazingTypeRightPart')}</CardTitle>
            <CardDescription className="text-xs">Select the desired glazing type for the right part</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glazingTypes.map(glazing => (
                <button
                  key={glazing.id}
                  onClick={() => onConfigChange('rightGlazingType', glazing.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.rightGlazingType === glazing.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {glazing.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={glazing.image}
                        alt={glazing.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.rightGlazingType === glazing.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlazingTypeName(glazing.id)}
                    </div>
                    {glazing.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlazingTypeDescription(glazing.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Exterior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.exteriorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.exteriorColor}
              onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>

        {/* Interior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.interiorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.interiorColor}
              onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SchuifpuiOptionsPanel;

